import { useCallback, useEffect } from 'react';

import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll';
import {
	useCommentsPanelActions,
	useCommentsPanelState,
	ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import {
	handleCommentsPanelNavigationWithViewChange,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils';
import { CommentsType } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery';
import { fg } from '@confluence/feature-gating';

export const useRegisterCommentsPanelCallbacks = () => {
	const { commentService } = useCommentService();
	const { isCommentsPanelShown, showCommentsPanel, hideCommentsPanel } = useShowCommentsPanel();
	const { setCurrentlySelectedThreadKey, setCurrentView } = useCommentsPanelActions();
	const { currentView } = useCommentsPanelState();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();

	const setCommentThreadHandler = useCallback(
		(threadId?: string) => {
			// highlight a comment in sidebar
			// if threadId is undefined, it means we are de-selecting
			setCurrentlySelectedThreadKey(threadId);
			if (threadId) {
				if (fg('confluence_comments_unified_navigation')) {
					// Use unified navigation when feature gate is enabled
					// content-types-bridge-comments only has inline comments
					let expectedView: ViewValues | undefined = undefined;
					if (currentView === ViewValues.RESOLVED) {
						expectedView = ViewValues.OPEN;
						setCurrentView && setCurrentView(ViewValues.OPEN);
					}
					handleCommentsPanelNavigationWithViewChange({
						scrollBehavior: ScrollBehavior.SMOOTH,
						context: NavigationContext.PANEL,
						block: 'center',
						annotationId: threadId,
						commentType: CommentsType.INLINE,
						annotationClicked: true,
						expectedView,
					});
				} else {
					// scroll to the comment thread in the sidebar
					scrollCommentsPanelToThread({
						annotationId: threadId,
						annotationClicked: true,
					});
				}
			}
		},
		[setCurrentlySelectedThreadKey, scrollCommentsPanelToThread, currentView, setCurrentView],
	);

	const showPanelHandler = useCallback(
		(threadId?: string) => {
			if (!isCommentsPanelShown) {
				showCommentsPanel();
			}
			if (threadId) {
				setCommentThreadHandler(threadId);
			}
		},
		[isCommentsPanelShown, showCommentsPanel, setCommentThreadHandler],
	);

	const hidePanelHandler = useCallback(() => {
		if (isCommentsPanelShown) {
			hideCommentsPanel();
		}
	}, [isCommentsPanelShown, hideCommentsPanel]);

	useEffect(() => {
		commentService.registerSelectCommentThreadHandler(setCommentThreadHandler);
		commentService.registerShowPanelHandler(showPanelHandler);
		commentService.registerHidePanelHandler(hidePanelHandler);
		return () => {
			// un-set the handlers on component unmount
			commentService.registerSelectCommentThreadHandler(undefined);
			commentService.registerShowPanelHandler(undefined);
			commentService.registerHidePanelHandler(undefined);
		};
	}, [commentService, showPanelHandler, hidePanelHandler, setCommentThreadHandler]);
};
